import { NetworkStatus, type OperationVariables, type QueryResult } from '@apollo/client'
import { useDeepMemo } from '@retailer-platform/shared-common'
import {
  type BasePendingResponse,
  type BaseSuccessResponse,
  type BaseErrorResponse,
} from './baseResponse'

export type BaseEagerResponse = {
  refetch: () => void
  networkStatus: NetworkStatus
}
export type EagerPendingResponse = BaseEagerResponse & BasePendingResponse
export type EagerSuccessResponse<TData> = BaseEagerResponse & BaseSuccessResponse<TData>
export type EagerErrorResponse = BaseEagerResponse & BaseErrorResponse
export type EagerResponse<TData = unknown> =
  | EagerPendingResponse
  | EagerSuccessResponse<TData>
  | EagerErrorResponse

export function useApolloRequestWrapper<
  TResult,
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables
>({
  query,
  mapResponse,
}: {
  query: QueryResult<TData, TVariables>
  mapResponse: (data: TData) => TResult
}): EagerResponse<TResult> {
  return useDeepMemo(() => {
    const { data, error, loading: isLoading, refetch, networkStatus } = query
    const baseResponse: BaseEagerResponse = {
      refetch,
      networkStatus,
    }

    if (error) {
      return {
        type: 'error',
        isLoading: false,
        error,
        ...baseResponse,
      }
    }
    if (networkStatus === NetworkStatus.ready && !data) {
      return {
        type: 'pending',
        isLoading: false,
        ...baseResponse,
      }
    }
    if (isLoading) {
      return {
        type: 'pending',
        isLoading: true,
        ...baseResponse,
      }
    }

    const parsedData = mapResponse(data!)
    return {
      type: 'success',
      data: parsedData,
      isLoading: false,
      ...baseResponse,
    }
  }, [query, mapResponse])
}
